import type {
  AddressBookEntry,
  AddressListResponse,
  AddressSearchResponse,
  BookRequestLtl,
  BookRequestParcel,
  BookResponse,
  CarrierAccount,
  ConnectCarrierPayload,
  CreateAddressPayload,
  CreateTemplatePayload,
  DashboardPayload,
  InviteTeamMemberPayload,
  ManagedApiKeyPayload,
  RateQuoteRequestLtl,
  RateQuoteRequestParcel,
  RateQuoteResponse,
  RevealedApiKeyPayload,
  RotateApiKeyPayload,
  SessionPayload,
  ShipmentDetail,
  ShipmentTemplatePayload,
  ShipmentsResponse,
  TeamListPayload,
  TeamMemberPayload,
  UpdateAddressPayload,
  UpdateCarrierPayload,
  UpdateMemberRolePayload,
  ReportsPayload,
  ReportGroupBy,
  CarrierScoresResponse,
  RequestCarrierPayload,
  WooCommerceAuthorizeApprovalPayload,
  WooCommerceAuthorizeApprovalResponse,
  WooCommerceIntegrationPayload,
  WooCommerceSettingsPayload,
  WooCommerceTestRatePayload,
} from "@/lib/api/types";
import type { AgentConnectionPayload, AgentDeviceRequestPayload } from "@/lib/agent/types";

type ApiEnvelope<T> = {
  status: "success" | "error";
  data?: T;
  error?: string;
  details?: string;
};

async function apiRequest<T>(input: string, init?: RequestInit): Promise<T> {
  const response = await fetch(input, {
    credentials: "include",
    headers: {
      "Content-Type": "application/json",
      ...(init?.headers ?? {}),
    },
    ...init,
  });

  let body: ApiEnvelope<T> | T | null = null;
  try {
    body = await response.json();
  } catch {
    body = null;
  }

  if (!response.ok) {
    if (body && typeof body === "object" && "error" in body) {
      const known = body as ApiEnvelope<T>;
      throw new Error(known.error ?? known.details ?? `Request failed: ${response.status}`);
    }
    throw new Error(`Request failed: ${response.status}`);
  }

  if (body && typeof body === "object" && "status" in body) {
    const envelope = body as ApiEnvelope<T>;
    if (envelope.status === "error") {
      throw new Error(envelope.error ?? envelope.details ?? "Unknown API error");
    }
    return envelope.data as T;
  }

  return body as T;
}

export const apiClient = {
  getSession: () => apiRequest<SessionPayload>("/api/session/me"),

  getAgentDeviceRequest: (userCode: string) =>
    apiRequest<AgentDeviceRequestPayload>(`/api/agent/device/requests/${encodeURIComponent(userCode)}`),

  approveAgentDeviceRequest: (userCode: string) =>
    apiRequest<{ approved: true }>("/api/agent/device/approve", {
      method: "POST",
      body: JSON.stringify({ user_code: userCode }),
    }),

  denyAgentDeviceRequest: (userCode: string) =>
    apiRequest<{ denied: true }>("/api/agent/device/deny", {
      method: "POST",
      body: JSON.stringify({ user_code: userCode }),
    }),

  getAgentConnections: () =>
    apiRequest<AgentConnectionPayload[]>("/api/agent/connections"),

  revokeAgentConnection: (connectionId: string) =>
    apiRequest<{ revoked: true }>(`/api/agent/connections/${encodeURIComponent(connectionId)}`, {
      method: "DELETE",
    }),

  disconnect: () =>
    apiRequest<{ disconnected: boolean }>("/api/session/disconnect", {
      method: "POST",
      body: JSON.stringify({}),
    }),

  getApiKeys: () => apiRequest<ManagedApiKeyPayload[]>("/api/account/api-keys"),

  revealApiKey: (id: string) =>
    apiRequest<RevealedApiKeyPayload>("/api/account/api-keys/reveal", {
      method: "POST",
      body: JSON.stringify({ id }),
    }),

  rotateApiKey: (id?: string) =>
    apiRequest<RotateApiKeyPayload>("/api/account/api-keys/rotate", {
      method: "POST",
      body: JSON.stringify(id ? { id } : {}),
    }),

  getWooCommerceIntegration: () =>
    apiRequest<WooCommerceIntegrationPayload>("/api/account/integrations/woocommerce/status"),

  approveWooCommerceConnection: (payload: WooCommerceAuthorizeApprovalPayload) =>
    apiRequest<WooCommerceAuthorizeApprovalResponse>("/api/account/integrations/woocommerce/authorize", {
      method: "POST",
      body: JSON.stringify(payload),
    }),

  updateWooCommerceSettings: (payload: Partial<WooCommerceSettingsPayload>) =>
    apiRequest<WooCommerceIntegrationPayload>("/api/account/integrations/woocommerce/settings", {
      method: "PATCH",
      body: JSON.stringify(payload),
    }),

  testWooCommerceRate: () =>
    apiRequest<WooCommerceTestRatePayload>("/api/account/integrations/woocommerce/test-rate", {
      method: "POST",
      body: JSON.stringify({}),
    }),

  disconnectWooCommerce: () =>
    apiRequest<{ disconnected: boolean; warning: string }>("/api/account/integrations/woocommerce/disconnect", {
      method: "POST",
      body: JSON.stringify({}),
    }),

  quoteLtl: (payload: RateQuoteRequestLtl) =>
    apiRequest<RateQuoteResponse>("/api/tms/rates/ltl", {
      method: "POST",
      body: JSON.stringify(payload),
    }),

  quoteRossiPreview: (payload: RateQuoteRequestLtl) =>
    apiRequest<RateQuoteResponse>("/api/tms/rates/rossi-preview", {
      method: "POST",
      body: JSON.stringify(payload),
    }),

  quoteParcel: (payload: RateQuoteRequestParcel) =>
    apiRequest<RateQuoteResponse>("/api/tms/rates/parcel", {
      method: "POST",
      body: JSON.stringify(payload),
    }),

  retrieveQuote: (refId: string) =>
    apiRequest<RateQuoteResponse>(`/api/tms/retrieve/${encodeURIComponent(refId)}`),

  bookLtl: (payload: BookRequestLtl) =>
    apiRequest<BookResponse>("/api/tms/book/ltl", {
      method: "POST",
      body: JSON.stringify(payload),
    }),

  bookParcel: (payload: BookRequestParcel) =>
    apiRequest<BookResponse>("/api/tms/book/parcel", {
      method: "POST",
      body: JSON.stringify(payload),
    }),

  cancelBooking: (payload: { bookingId?: number; pickupNumber?: string; carrierCode?: string }) =>
    apiRequest<{ status?: string; message?: string }>("/api/tms/book/cancel", {
      method: "POST",
      body: JSON.stringify(payload),
    }),

  trackShipment: async (trackingNumber: string, carrier: string) => {
    const raw = await apiRequest<
      Array<{
        createdDate?: string;
        eventDate?: string;
        eventTime?: string;
        eventType?: string;
        summary?: string;
        geoCode?: string;
      }>
    >(`/api/tms/track/${encodeURIComponent(trackingNumber)}?carrier=${encodeURIComponent(carrier)}`);
    return raw.map((e) => ({
      timestamp: e.createdDate ?? (e.eventDate && e.eventTime ? `${e.eventDate}T${e.eventTime}` : e.eventDate),
      status: e.eventType,
      location: e.geoCode || undefined,
      description: e.summary,
    }));
  },

  getDashboard: () => apiRequest<DashboardPayload>("/api/tms/dashboard"),

  getShipments: (params: {
    page?: number;
    pageSize?: number;
    status?: string;
    mode?: string;
    carrier?: string;
    from?: string;
    to?: string;
    search?: string;
    sort?: "created_date" | "total_cost" | "status";
    sortDir?: "asc" | "desc";
  }) => {
    const query = new URLSearchParams();
    for (const [key, value] of Object.entries(params)) {
      if (value !== undefined && value !== null && String(value).trim() !== "") {
        query.set(key, String(value));
      }
    }
    return apiRequest<ShipmentsResponse>(`/api/tms/shipments?${query.toString()}`);
  },

  getShipmentById: (id: string) => apiRequest<ShipmentDetail>(`/api/tms/shipments/${encodeURIComponent(id)}`),

  getCarriers: (options?: { includeInactive?: boolean }) => {
    const query = new URLSearchParams();
    if (options?.includeInactive) {
      query.set("includeInactive", "true");
    }
    const suffix = query.size > 0 ? `?${query.toString()}` : "";
    return apiRequest<CarrierAccount[]>(`/api/tms/carriers${suffix}`);
  },

  connectCarrier: (payload: ConnectCarrierPayload) =>
    apiRequest<CarrierAccount>("/api/tms/carriers/connect", {
      method: "POST",
      body: JSON.stringify(payload),
    }),

  connectDemoCarrier: () =>
    apiRequest<CarrierAccount[]>("/api/tms/carriers/demo", {
      method: "POST",
      body: JSON.stringify({}),
    }),

  updateCarrier: (id: string, payload: UpdateCarrierPayload) =>
    apiRequest<CarrierAccount>(`/api/tms/carriers/${encodeURIComponent(id)}/edit`, {
      method: "POST",
      body: JSON.stringify(payload),
    }),

  enableCarrier: (id: string) =>
    apiRequest<CarrierAccount>(`/api/tms/carriers/${encodeURIComponent(id)}/enable`, {
      method: "POST",
      body: JSON.stringify({}),
    }),

  disableCarrier: (id: string) =>
    apiRequest<CarrierAccount>(`/api/tms/carriers/${encodeURIComponent(id)}/disable`, {
      method: "POST",
      body: JSON.stringify({}),
    }),

  requestCarrier: (payload: RequestCarrierPayload) =>
    apiRequest<{ message: string }>("/api/tms/carriers/request", {
      method: "POST",
      body: JSON.stringify(payload),
    }),

  // Templates
  getTemplates: () => apiRequest<ShipmentTemplatePayload[]>("/api/tms/templates"),

  createTemplate: (payload: CreateTemplatePayload) =>
    apiRequest<ShipmentTemplatePayload>("/api/tms/templates", {
      method: "POST",
      body: JSON.stringify(payload),
    }),

  updateTemplate: (id: string, payload: CreateTemplatePayload) =>
    apiRequest<ShipmentTemplatePayload>(`/api/tms/templates/${encodeURIComponent(id)}`, {
      method: "PUT",
      body: JSON.stringify(payload),
    }),

  deleteTemplate: (id: string) =>
    apiRequest<{ deleted: true }>(`/api/tms/templates/${encodeURIComponent(id)}`, {
      method: "DELETE",
    }),

  toggleTemplateFavorite: (id: string) =>
    apiRequest<ShipmentTemplatePayload>(`/api/tms/templates/${encodeURIComponent(id)}/favorite`, {
      method: "POST",
      body: JSON.stringify({}),
    }),

  exportTemplates: () =>
    apiRequest<{ templates: ShipmentTemplatePayload[]; exported_at: string }>("/api/tms/templates/export"),

  importTemplates: (payload: { templates: CreateTemplatePayload[] }) =>
    apiRequest<{ imported: number }>("/api/tms/templates/import", {
      method: "POST",
      body: JSON.stringify(payload),
    }),

  // Addresses
  getAddresses: (options?: { search?: string; favorites?: boolean }) => {
    const query = new URLSearchParams();
    if (options?.search) query.set("search", options.search);
    if (options?.favorites) query.set("favorites", "true");
    const suffix = query.size > 0 ? `?${query.toString()}` : "";
    return apiRequest<AddressListResponse>(`/api/tms/addresses${suffix}`);
  },

  searchAddresses: (q: string) =>
    apiRequest<AddressSearchResponse>(`/api/tms/addresses/search?q=${encodeURIComponent(q)}`),

  createAddress: (payload: CreateAddressPayload) =>
    apiRequest<AddressBookEntry>("/api/tms/addresses", {
      method: "POST",
      body: JSON.stringify(payload),
    }),

  updateAddress: (id: string, payload: UpdateAddressPayload) =>
    apiRequest<AddressBookEntry>(`/api/tms/addresses/${encodeURIComponent(id)}`, {
      method: "PUT",
      body: JSON.stringify(payload),
    }),

  deleteAddress: (id: string) =>
    apiRequest<{ deleted: true; id: string }>(`/api/tms/addresses/${encodeURIComponent(id)}`, {
      method: "DELETE",
    }),

  validateAddress: (id: string) =>
    apiRequest<{ address: AddressBookEntry; validation: { valid: boolean; error: string | null } }>(
      `/api/tms/addresses/${encodeURIComponent(id)}/validate`,
      { method: "POST", body: JSON.stringify({}) },
    ),

  toggleAddressFavorite: (id: string) =>
    apiRequest<AddressBookEntry>(`/api/tms/addresses/${encodeURIComponent(id)}/favorite`, {
      method: "POST",
      body: JSON.stringify({}),
    }),

  exportAddressesCsv: () =>
    fetch("/api/tms/addresses/export", { credentials: "include" }).then((r) => {
      if (!r.ok) throw new Error(`Export failed: ${r.status}`);
      return r.blob();
    }),

  // Shipment documents
  getShipmentLabelPdf: (id: string) =>
    fetch(`/api/tms/shipments/${encodeURIComponent(id)}/label`, { credentials: "include" }).then(async (r) => {
      if (!r.ok) {
        const body = await r.json().catch(() => null);
        throw new Error((body as { error?: string } | null)?.error || `Label generation failed: ${r.status}`);
      }
      return r.blob();
    }),

  getShipmentBolPdf: (id: string) =>
    fetch(`/api/tms/shipments/${encodeURIComponent(id)}/bol`, { credentials: "include" }).then(async (r) => {
      if (!r.ok) {
        const body = await r.json().catch(() => null);
        throw new Error((body as { error?: string } | null)?.error || `BOL generation failed: ${r.status}`);
      }
      return r.blob();
    }),

  // Onboarding
  completeOnboarding: () =>
    apiRequest<{ completedAt: string }>("/api/onboarding/complete", {
      method: "PATCH",
      body: JSON.stringify({}),
    }),

  // Team management
  getTeamMembers: () => apiRequest<TeamListPayload>("/api/account/team"),

  inviteTeamMember: (payload: InviteTeamMemberPayload) =>
    apiRequest<{ member: TeamMemberPayload }>("/api/account/team/invite", {
      method: "POST",
      body: JSON.stringify(payload),
    }),

  removeTeamMember: (memberId: string) =>
    apiRequest<{ removed: true; memberId: string }>(`/api/account/team/${encodeURIComponent(memberId)}`, {
      method: "DELETE",
    }),

  updateMemberRole: (memberId: string, payload: UpdateMemberRolePayload) =>
    apiRequest<{ member: TeamMemberPayload }>(`/api/account/team/${encodeURIComponent(memberId)}/role`, {
      method: "PUT",
      body: JSON.stringify(payload),
    }),

  // Reports
  getReports: (params: { from?: string; to?: string; groupBy?: ReportGroupBy }) => {
    const query = new URLSearchParams();
    for (const [key, value] of Object.entries(params)) {
      if (value !== undefined && value !== null && String(value).trim() !== "") {
        query.set(key, String(value));
      }
    }
    return apiRequest<ReportsPayload>(`/api/tms/reports?${query.toString()}`);
  },

  exportReportsCsv: (params: { from?: string; to?: string; groupBy?: ReportGroupBy }) => {
    const query = new URLSearchParams();
    for (const [key, value] of Object.entries(params)) {
      if (value !== undefined && value !== null && String(value).trim() !== "") {
        query.set(key, String(value));
      }
    }
    return fetch(`/api/tms/reports/export?${query.toString()}`, { credentials: "include" }).then((r) => {
      if (!r.ok) throw new Error(`Export failed: ${r.status}`);
      return r.blob();
    });
  },

  // Carrier Performance Scores (AI Recommendations)
  getCarrierScores: () => apiRequest<CarrierScoresResponse>("/api/tms/carrier-scores"),
};
